import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableSortLabel } from "@mui/material";
import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { useSelector } from "react-redux";

interface Props {
  rows: any[];
}

const MetricMonthTable = ({ rows }: Props) => {
  const [sort, setSort] = useState<{
    keytoSort: string | undefined;
    direction: "asc" | "desc" | undefined;
  }>({
    keytoSort: "modified",
    direction: "desc",
  });

  const tableheaders = [
    { id: 0, KEY: "Period", label: "Period", sortable: false },
    { id: 1, KEY: "Forecast", label: "Forecast", sortable: true },
    { id: 2, KEY: "Actual", label: "Actual", sortable: true },
    { id: 3, KEY: "Deviation", label: "Deviation", sortable: true },
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    setSort({
      keytoSort: header.KEY,
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;
        if (keytoSort === undefined) {
          return 0;
        }
        let aValue, bValue;

        if (keytoSort === "month") {
          const [monthA, yearA] = a[keytoSort].split("/").map(Number);
          const [monthB, yearB] = b[keytoSort].split("/").map(Number);
          aValue = yearA * 100 + monthA;
          bValue = yearB * 100 + monthB;
        } else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  const sortedRows = getSortedData(rows || []);

  // Check if all values for Actual, Forecast, and Deviation are null or empty
  const allValuesNull = sortedRows.every(
    (row) =>
      (row.Actual === null || row.Actual === "") &&
      (row.Forecast === null || row.Forecast === "") &&
      (row.Deviation === null || row.Deviation === "")
  );

  const selectedMetric = useSelector((state: any) => state.forecasters?.selectedMetric);

  const isPercentageMetric = () => {
    const percentageMetrics = ['growth', 'cvr', 'avg_rs_p', 'churn_rate', 'rs_br_ng', 'ratio_between_acquired_to_ftd'];
    return percentageMetrics.includes(selectedMetric?.toLowerCase());
  };

  const formatValue = (value: any) => {
    if (value === null || value === "" || isNaN(value)) return "0%";
    return isPercentageMetric() ? `${Math.round(Number(value) * 100)}%` : Math.round(Number(value));
  };

  return (
    <>
      <TableContainer
        sx={{ maxHeight: 540, scrollbarWidth: "thin", marginTop: "16px" }}
      >
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header, index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allValuesNull ? (
              <TableRow>
                <TableCell colSpan={tableheaders.length} align="center">
                  <Typography variant="h6">No Data Available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row: any, rowIndex: any) => (
                <React.Fragment key={rowIndex}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    <TableCell align="left">{row.Period}</TableCell>
                    <TableCell align="left">
                      {formatValue(row.Forecast)}
                    </TableCell>
                    <TableCell align="left">
                      {formatValue(row.Actual)}
                    </TableCell>
                    <TableCell align="left">
                      {`${Math.round(Number(row?.Deviation * 100))}%`}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default MetricMonthTable;
